import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import "./Order.css";
import {BASE_URL} from "./Constants";

const Order = () => {
  const [orders, setOrders] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [shippingDetails, setShippingDetails] = useState({});
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const safeToFixed = (value, digits = 2) =>
    typeof value === "number" && !isNaN(value) ? value.toFixed(digits) : "0.00";

  // ✅ Fetch logged-in user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/get_user_details`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Not logged in");
        const data = await res.json();
        setUser(data);
      } catch (err) {
        navigate("/checkout/login");
      }
    };
    fetchUser();
  }, [navigate]);

  // ✅ Fetch orders
  useEffect(() => {
    if (!user) return;
    const fetchOrders = async () => {
      try {
        const res = await fetch(`${BASE_URL}/fetch_orders/${user.user_id}`, {
          method: "GET",
          credentials: "include",
        });
        const data = await res.json();
        setOrders(data.orders || []);
      } catch (err) {
        console.error("Order fetch error:", err);
      }
    };
    fetchOrders();
  }, [user]);

  // ✅ Fetch all shipping details once orders load
  useEffect(() => {
    if (orders.length === 0) return;

    const fetchAllShipping = async () => {
      const newShipping = {};
      for (const order of orders) {
        try {
          const res = await fetch(`${BASE_URL}/shipping_details/${order.order_id}`, {
            credentials: "include",
          });

          if (res.ok) {
            const data = await res.json();
            const basePrice = data.base_price || 0;
            const gstPerc = data.gst_percentage || 0;
            const gstAmt = (basePrice * gstPerc) / 100;
            const totalCost = basePrice + gstAmt;

            newShipping[order.order_id] = {
              ...data,
              base_price: basePrice,
              gst_percentage: gstPerc,
              gst_amount: gstAmt,
              total_cost: totalCost,
            };
          } else {
            newShipping[order.order_id] = null;
          }
        } catch {
          newShipping[order.order_id] = null;
        }
      }
      setShippingDetails(newShipping);
    };

    fetchAllShipping();
  }, [orders]);

  // ✅ Toggle order details
  const handleToggleDetails = async (orderId) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
      return;
    }

    setExpandedOrderId(orderId);

    if (!orderDetails[orderId]) {
      const res = await fetch(`${BASE_URL}/fetch_order/${orderId}`, {
        credentials: "include",
      });
      const data = await res.json();
      setOrderDetails((prev) => ({ ...prev, [orderId]: data }));
    }
  };

  // ✅ Compute total including shipping
  const getGrandTotal = (order) => {
    const shipping = shippingDetails[order.order_id];
    const shippingCost = shipping?.total_cost || 0;
    return order.total + shippingCost;
  };

  return (
    <>
      <div className="order-container">
        <h2>My Orders</h2>
        {orders.length === 0 ? (
          <p className="no-orders">No orders yet.</p>
        ) : (
          <table className="order-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date</th>
                <th>Status</th>
                <th>Total (₹)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const grandTotal = getGrandTotal(order);
                return (
                  <React.Fragment key={order.order_id}>
                    <tr>
                      <td>{order.order_id}</td>
                      <td>{new Date(order.creation_date).toLocaleString()}</td>
                      <td>{order.order_status}</td>
                      <td>₹ {safeToFixed(grandTotal)}</td>
                      <td>
                        <button
                          className="view-details-btn"
                          onClick={() => handleToggleDetails(order.order_id)}
                        >
                          {expandedOrderId === order.order_id
                            ? "Hide Details"
                            : "View Details"}
                        </button>
                      </td>
                    </tr>

                    {/* ✅ Expanded Order Details */}
                    {expandedOrderId === order.order_id && (
                      <tr className="expanded-row">
                        <td colSpan="5">
                          <div className="details-section">
                            <h3>Your Order</h3>

                            {orderDetails[order.order_id] ? (
                              <table className="order-items-table">
                                <thead>
                                  <tr>
                                    <th>Title</th>
                                    <th>Product</th>
                                    <th>Qty</th>
                                    <th>Unit Price</th>
                                    <th>GST %</th>
                                    <th>GST Amt</th>
                                    <th>Subtotal</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {orderDetails[order.order_id].items.map((item, i) => {
                                    const gstAmt =
                                      (item.price * item.quantity * item.gst_percentage) /
                                      100;
                                    const subTotal = item.price * item.quantity;
                                    return (
                                      <tr key={i}>
                                        <td
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            navigate(`/Product/${item.item_id}`)
                                          }
                                        >
                                          {item.image_id ? (                                      
                                            <img
                                              src={
                                                item.image_id
                                                  ? `${BASE_URL}/download_image/${item.image_id}`
                                                  : "/assets/default.jpg"
                                              }
                                              alt={item.name}
                                              style={{ width: "60px", height: "60px", objectFit: "cover" }}
                                            />
                                          ) : (
                                            "No Image"
                                          )}
                                        </td>
                                        <td
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            navigate(`/Product/${item.item_id}`)
                                          }
                                        >
                                          {item.name}
                                        </td>
                                        <td>{item.quantity}</td>
                                        <td>₹ {safeToFixed(item.price)}</td>
                                        <td>{item.gst_percentage}%</td>
                                        <td>₹ {safeToFixed(gstAmt)}</td>
                                        <td>₹ {safeToFixed(subTotal)}</td>
                                      </tr>
                                    );
                                  })}

                                  {/* ✅ Shipping Row */}
                                  {shippingDetails[order.order_id] && (
                                    <tr>
                                      <td>🚚</td>
                                      <td>
                                        <strong>
                                          Shipping ({shippingDetails[order.order_id].type})
                                        </strong>
                                      </td>
                                      <td>1</td>
                                      <td>
                                        ₹{" "}
                                        {safeToFixed(
                                          shippingDetails[order.order_id].base_price
                                        )}
                                      </td>
                                      <td>
                                        {shippingDetails[order.order_id]
                                          .gst_percentage || 0}
                                        %
                                      </td>
                                      <td>
                                        ₹{" "}
                                        {safeToFixed(
                                          shippingDetails[order.order_id].gst_amount
                                        )}
                                      </td>
                                      <td>
                                        ₹{" "}
                                        {safeToFixed(
                                          shippingDetails[order.order_id].total_cost
                                        )}
                                      </td>
                                    </tr>
                                  )}

                                  {/* ✅ Grand Total Row */}
                                  <tr className="grand-total-row">
                                    <td colSpan="6" style={{ textAlign: "right" }}>
                                      <strong style={{ color: "#8000ff" }}>Grand Total:</strong>
                                    </td>
                                    <td>
                                      <strong style={{ color: "#8000ff" }}>₹ {safeToFixed(grandTotal)}</strong>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            ) : (
                              <p>Loading order details...</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <Footer />
    </>
  );
};

export default Order;